import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  UserGroupIcon, 
  AcademicCapIcon,
  TrophyIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { getApiUrl } from '../../utils/api';
import ImageModal from '../../components/ImageModal';
import { getImageUrlWithEnv } from '../../utils/imageUrl';

const Coaches = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        setLoading(true);
        const response = await fetch(getApiUrl('/api/coaching-staff/'));
        if (!response.ok) {
          throw new Error('Failed to fetch coaches');
        }
        const data = await response.json();
        
        // Filter to get youth coaches using new team assignment system
        const youthCoaches = data.filter(coach => {
          // Check if coach has youth academy team assignments
          if (coach.team_assignments && coach.team_assignments.length > 0) {
            return coach.team_assignments.some(assignment => 
              assignment.team_type === 'youth_academy'
            );
          }
          
          // Fallback to old system for backward compatibility
          return coach.team_type === 'youth_academy' ||
                 coach.position.includes('U9') || 
                 coach.position.includes('U10') || 
                 coach.position.includes('U11') || 
                 coach.position.includes('U12') || 
                 coach.position.includes('U13') || 
                 coach.position.includes('U14') || 
                 coach.position.includes('U15') || 
                 coach.position.includes('U17') ||
                 coach.position.includes('U19') ||
                 coach.position.includes('U21') ||
                 coach.position.includes('ახალგაზრდული') ||
                 coach.position.includes('აკადემია');
        });
        
        setCoaches(youthCoaches);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching coaches:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCoaches();
  }, []);

  const ageGroups = ["U9", "U10", "U11", "U12", "U13", "U14", "U15", "U17", "U19", "U21", "ყველა U კატეგორია"];
  
  const getCoachesByAgeGroup = (ageGroup) => {
    if (ageGroup === "ყველა U კატეგორია") {
      return coaches.filter(coach => 
        coach.position.includes('მეკარეების მწვრთნელი') ||
        (coach.team_assignments && coach.team_assignments.some(assignment => 
          assignment.team_type === 'youth_academy' && 
          (assignment.u_category === 'general' || assignment.u_category === null)
        ))
      );
    }
    
    return coaches.filter(coach => {
      // Check new team assignment system first
      if (coach.team_assignments && coach.team_assignments.length > 0) {
        return coach.team_assignments.some(assignment => 
          assignment.team_type === 'youth_academy' && 
          assignment.u_category === ageGroup
        );
      }
      
      // Fallback to old position-based system
      return coach.position.includes(ageGroup);
    });
  };

  const getLicenseColor = (license) => {
    switch (license) {
      case 'A': return 'bg-green-600';
      case 'B': return 'bg-blue-600';
      case 'C': return 'bg-purple-600';
      default: return 'bg-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-georgian">მონაცემების ჩატვირთვა...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-georgian mb-4">შეცდომა: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded font-georgian"
          >
            ხელახლა ცდა
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            ასაკობრივი ჯგუფების მწვრთნელები
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი ასაკობრივი ჯგუფების პროფესიონალური მწვრთნელთა კორპუსი
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <UserGroupIcon className="h-8 w-8 text-primary-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">მწვრთნელები</h3>
            <p className="text-2xl font-bold text-primary-600">{coaches.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <AcademicCapIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">ასაკობრივი ჯგუფები</h3>
            <p className="text-2xl font-bold text-blue-600">{ageGroups.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <TrophyIcon className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">საშუალო გამოცდილება</h3>
            <p className="text-2xl font-bold text-yellow-600">
              {coaches.length > 0 ? Math.round(coaches.reduce((sum, coach) => {
                const exp = parseInt(coach.experience) || 0;
                return sum + exp;
              }, 0) / coaches.length) : 0} წელი
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="h-8 w-8 bg-green-600 rounded-full mx-auto mb-2 flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 font-georgian">A ლიცენზია</h3>
            <p className="text-2xl font-bold text-green-600">{coaches.filter(c => c.license === 'A').length}</p>
          </div>
        </div>

        {/* Coaches by Age Group */}
        {ageGroups.map((ageGroup) => {
          const groupCoaches = getCoachesByAgeGroup(ageGroup);
          if (groupCoaches.length === 0) return null;
          
          return (
            <div key={ageGroup} className="bg-white rounded-lg shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
                {ageGroup === "ყველა U კატეგორია" ? "ყველა U კატეგორიის მწვრთნელი" : `${ageGroup} მწვრთნელები`} ({groupCoaches.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {groupCoaches.map((coach, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105">
                    {/* Coach Photo */}
                    <div className="relative h-48 bg-gray-200 cursor-pointer" onClick={() => coach.photo_url && setSelectedImage({
                      imageUrl: getImageUrlWithEnv(coach.photo_url),
                      alt: coach.name,
                      title: coach.name,
                      subtitle: `${coach.position} • ${coach.nationality}`
                    })}>
                      {coach.photo_url ? (
                        <img
                          src={getImageUrlWithEnv(coach.photo_url)}
                          alt={coach.name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-full h-full ${coach.photo_url ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-green-500 to-green-700`}
                      >
                        <UserIcon className="h-16 w-16 text-white" />
                      </div>
                    </div>
                    
                    {/* Coach Info */}
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 font-georgian text-lg mb-1">{coach.name}</h3>
                      <p className="text-sm text-green-600 font-georgian mb-2">{coach.position}</p>
                      
                      <div className="space-y-1 text-sm text-gray-600 font-georgian">
                        {coach.age && (
                          <p>ასაკი: {coach.age} წელი</p>
                        )}
                        <p>{coach.nationality}</p>
                        {coach.experience && (
                          <p>გამოცდილება: {coach.experience}</p>
                        )}
                        {coach.license && (
                          <div className="flex items-center justify-center mt-3">
                            <span className={`inline-flex items-center ${getLicenseColor(coach.license)} text-white text-xs font-medium px-3 py-1 rounded-full`}>
                              {coach.license} ლიცენზია
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {coach.description && (
                        <p className="mt-3 text-xs text-gray-500 font-georgian line-clamp-2">{coach.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* License Distribution */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            ლიცენზიების განაწილება
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3">
                A
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian">A ლიცენზია</h3>
              <p className="text-2xl font-bold text-green-600">{coaches.filter(c => c.license === 'A').length}</p>
              <p className="text-sm text-gray-600 font-georgian">უმაღლესი კატეგორია</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3">
                B
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian">B ლიცენზია</h3>
              <p className="text-2xl font-bold text-blue-600">{coaches.filter(c => c.license === 'B').length}</p>
              <p className="text-sm text-gray-600 font-georgian">საშუალო კატეგორია</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3">
                C
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian">C ლიცენზია</h3>
              <p className="text-2xl font-bold text-purple-600">{coaches.filter(c => c.license === 'C').length}</p>
              <p className="text-sm text-gray-600 font-georgian">ძირითადი კატეგორია</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="text-center">
          <Link
            to="/youth"
            className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
          >
            ← უკან ახალგაზრდულ გვერდზე
          </Link>
        </div>
      </div>
      
      {/* Image Modal */}
      <ImageModal
        isOpen={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage?.imageUrl}
        alt={selectedImage?.alt}
        title={selectedImage?.title}
        subtitle={selectedImage?.subtitle}
      />
    </div>
  );
};

export default Coaches;
